import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ClientKanbanCard } from './ClientKanbanCard';
import { Client } from '@/types/client';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Plus, MoreHorizontal } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ColumnData {
  id: string;
  title: string;
  status: Client['status'];
  color: string;
  bgColor: string;
}

interface KanbanColumnProps {
  column: ColumnData;
  clients: Client[];
}

interface ResponsiveKanbanColumnProps extends KanbanColumnProps {
  viewMode: 'comfortable' | 'compact' | 'spacious';
}

export const KanbanColumn: React.FC<ResponsiveKanbanColumnProps> = ({ 
  column, 
  clients, 
  viewMode = 'comfortable' 
}) => {
  const {
    setNodeRef,
    isOver,
  } = useDroppable({
    id: column.status,
  });

  const clientIds = clients.map(client => client.id);

  return (
    <Card 
      className={cn(
        "h-full flex flex-col transition-all duration-300 ease-in-out",
        "border-2 bg-card/50 backdrop-blur-sm w-full",
        isOver && "border-primary ring-2 ring-primary/20 shadow-lg transform scale-[1.01]"
      )}
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <div className="flex items-center gap-3">
          <div 
            className={cn("w-4 h-4 rounded-full", column.bgColor)}
            style={{ backgroundColor: column.color }}
          />
          <h3 className="font-semibold text-base">{column.title}</h3>
          <Badge variant="secondary" className="ml-auto">
            {clients.length}
          </Badge>
        </div>
        
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <Plus className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="flex-1 overflow-hidden p-0">
        <div
          ref={setNodeRef}
          className={cn(
            "h-full overflow-y-auto overflow-x-hidden px-4 pb-4",
            "scrollbar-thin scrollbar-thumb-muted-foreground/20 scrollbar-track-transparent",
            isOver && "bg-muted/10"
          )}
        >
          <SortableContext 
            items={clientIds} 
            strategy={verticalListSortingStrategy}
          >
            <div className={cn(
              viewMode === 'compact' ? 'space-y-2' : 
              viewMode === 'spacious' ? 'space-y-4' : 'space-y-3'
            )}>
              {clients.map((client) => (
                <ClientKanbanCard
                  key={client.id}
                  client={client}
                  viewMode={viewMode}
                />
              ))}
              
              {clients.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <div className="w-16 h-16 rounded-full bg-muted/20 flex items-center justify-center mb-3">
                    <Plus className="h-8 w-8" />
                  </div>
                  <p className="text-sm text-center">لا توجد عملاء في هذه الحالة</p>
                  <p className="text-xs text-center mt-1">اسحب العملاء هنا</p>
                </div>
              )}
            </div>
          </SortableContext>
        </div>
      </CardContent>
    </Card>
  );
};